import {
  NotificationPreferenceUpdatePayload,
  NotificationPreferences,
} from "../../models/notification-preferences";

import { Database } from "../../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@punchup/sentry";

export async function getUserNotificationPreferences(
  supabaseAdmin: SupabaseClient<Database>,
  userId: string
): Promise<NotificationPreferences | null> {
  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to fetch notification preferences"
    );
  }

  const { status, data, error } = await supabaseAdmin
    .from("notification_preferences_alt")
    .select(
      `new_comedian, new_special, new_tour_date, newsletter_email_frequency, has_opted_in_sms, new_subscription_content, push_notifications_enabled, manager_outreach`
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    logException(error, {
      data: "Database error while attempting to retrieve notification preferences",
    });
  } else if (status === 200) {
    return data;
  }
  throw new Error(
    `Error getting notification preferences for user with id: ${userId}`
  );
}

export async function updateNotificationPreferences({
  supabaseAdmin,
  payload,
  userId,
}: {
  supabaseAdmin: SupabaseClient<Database>;
  payload: NotificationPreferenceUpdatePayload;
  userId?: string;
}): Promise<boolean> {
  if (!userId) {
    throw new Error(
      "Unable to read userId when attempting to update notification preferences"
    );
  }

  const { count, status, error } = await supabaseAdmin
    .from("notification_preferences_alt")
    .update(payload, {
      count: "exact",
    })
    .eq("user_id", userId);

  if (error) {
    logException(error, {
      data: "Database error while attempting to update notification preferences",
    });
  } else if (status === 204) {
    if (count === 1) {
      return true;
    } else {
      const error = `Error while attempting to update notification preferences for user with id ${userId}. Row count was: ${count}.`;
      logException(error);
    }
  } else {
    const error = `Error while attempting to update notification preferences. Status was: ${status}.`;
    logException(error);
  }
  return false;
}

export async function updatePartialNotificationPreferencesForUser(
  supabaseAdmin: SupabaseClient<Database>,
  userId: string,
  payload: Partial<NotificationPreferenceUpdatePayload>
): Promise<NotificationPreferences> {
  const { data, error } = await supabaseAdmin
    .from("notification_preferences_alt")
    .update(payload)
    .eq("user_id", userId)
    .select(
      `new_comedian, new_special, new_tour_date, newsletter_email_frequency, has_opted_in_sms, new_subscription_content, push_notifications_enabled, manager_outreach`
    )
    .single();

  if (error) {
    logException(error, {
      data: "Database error while attempting to update notification preferences",
    });
    throw error;
  }

  return data;
}

export type SmsCustomMessagingEnrollmentResult =
  | "enrolled"
  | "already_enrolled"
  | "no_phone";

/**
 * Enroll a user in SMS custom messaging (marketing/comedian-blast SMS) if
 * they have a phone number on file.
 *
 * IMPORTANT — two-column trap: this writes ONLY
 * `notification_preferences_alt.has_opted_in_sms_custom_messaging` (the
 * marketing/prune-gate column). It must never write the sibling
 * `has_opted_in_sms` column (auth-flow/transactional).
 *
 * `notification_preferences_alt` has no INSERT RLS policy, so the
 * missing-row upsert branch below requires the caller to pass a
 * service-role admin client.
 *
 * Callers are expected to catch and log any thrown error themselves — this
 * function does not call `logException` so the caller owns a single Sentry
 * event per invocation site.
 */
export async function enrollUserInSmsCustomMessaging(
  supabaseAdmin: SupabaseClient<Database>,
  userId: string
): Promise<SmsCustomMessagingEnrollmentResult> {
  const { data: userExtra, error: userExtraError } = await supabaseAdmin
    .from("users_extra")
    .select("phone")
    .eq("id", userId)
    .maybeSingle();

  if (userExtraError) {
    throw userExtraError;
  }

  if (!userExtra?.phone?.trim()) {
    return "no_phone";
  }

  const { data: prefs, error: prefsError } = await supabaseAdmin
    .from("notification_preferences_alt")
    .select("has_opted_in_sms_custom_messaging")
    .eq("user_id", userId)
    .maybeSingle();

  if (prefsError) {
    throw prefsError;
  }

  if (prefs === null) {
    const { error: upsertError } = await supabaseAdmin
      .from("notification_preferences_alt")
      .upsert(
        { user_id: userId, has_opted_in_sms_custom_messaging: true },
        { onConflict: "user_id", ignoreDuplicates: true }
      );

    if (upsertError) {
      throw upsertError;
    }

    return "enrolled";
  }

  if (prefs.has_opted_in_sms_custom_messaging === true) {
    return "already_enrolled";
  }

  const { error: updateError } = await supabaseAdmin
    .from("notification_preferences_alt")
    .update({ has_opted_in_sms_custom_messaging: true })
    .eq("user_id", userId)
    .eq("has_opted_in_sms_custom_messaging", false);

  if (updateError) {
    throw updateError;
  }

  return "enrolled";
}
